import React, { FC, LazyExoticComponent, Suspense, useContext, useEffect, useRef } from 'react';
import { AppContext } from '../window';
import { lazyWithRetry } from '../utils/chunkLoad';
import { setBaseTitle, setCanonical, setMetaDescription } from '../utils/documentHead';
import { useEntryModals } from '../about/useEntryModals';
import HomePage from '../about/HomePage';
import { SiteContext } from './SiteContext';
import { CustomFrontendProps, SiteModuleProps, SitePageMeta } from './types';

type SiteComponent = LazyExoticComponent<FC<SiteModuleProps>>;
const siteComponents = new Map<string, SiteComponent>();

/**
 * The site module of a domain, one webpack chunk per site directory
 * (`site-<domain>-index-tsx`, which PHP preloads, see
 * `CustomFrontend::chunkFiles()`), so no other whitelabel downloads it.
 * `lazyWithRetry` retries a failed chunk once; a second failure reaches the
 * root ErrorBoundary, which reloads once and then shows its fallback.
 */
export const siteComponent = (domain: string): SiteComponent => {
  let component = siteComponents.get(domain);
  if (!component) {
    component = lazyWithRetry<FC<SiteModuleProps>>(
      () => import(/* webpackChunkName: "site-[request]" */ `../sites/${domain}/index.tsx`)
    );
    siteComponents.set(domain, component);
  }
  return component;
};

interface Props {
  site: CustomFrontendProps;
  page: SitePageMeta;
}

/**
 * A page of the whitelabel's custom frontend (`src/sites/<domain>`): the
 * site's own component for guests, with the signup/login modals and deep
 * links of the generic home page; members get what `/` gives them
 * (`HomePage`, which redirects through AuthRedirect).
 */
const CustomFrontendPage: FC<Props> = ({ site, page }) => {
  const { user } = useContext(AppContext);
  const { openSignup, openLogin, modals } = useEntryModals();

  // Head texts on client-side navigation; PHP rendered them for the first page.
  useEffect(() => {
    setBaseTitle(page.title);
    setMetaDescription(page.description);
    setCanonical(window.location.origin + page.path);
    return () => {
      setBaseTitle(site.defaultTitle);
      setMetaDescription(site.defaultDescription);
      setCanonical(null);
    };
  }, [page, site]);

  // A new page starts at the top, as a page load would
  const firstPath = useRef(page.path);
  useEffect(() => {
    if (page.path !== firstPath.current) {
      firstPath.current = '';
      window.scrollTo(0, 0);
    }
  }, [page.path]);

  if (user) {
    return <HomePage />;
  }

  const Site = siteComponent(site.domain);
  return (
    <SiteContext.Provider value={{ openSignup, openLogin }}>
      {/* Holds the viewport until the site chunk arrives, so the footer does not jump */}
      <Suspense fallback={<div className="custom-frontend-loading" aria-busy="true" />}>
        <Site path={page.path} />
      </Suspense>
      {modals}
    </SiteContext.Provider>
  );
};

export default CustomFrontendPage;
